import { useMemo, useState, type ReactNode } from "react";
import {
  BUFFER_ROLE_NAMES,
  BUFFER_ROLES,
  CUSTOM_UNIT_LIMITS_MM,
  DEFAULT_RADIATOR_MOUNTING_HEIGHT_MM,
  FITTING_KINDS,
  FITTING_NAMES,
  GENERIC_BUFFERS,
  GENERIC_CYLINDERS,
  GENERIC_VESSEL_SOURCE,
  OUTDOOR_UNITS,
  PANEL_TYPE_ALIASES,
  PIPE_CIRCUITS,
  PIPE_DIAMETERS_MM,
  PIPE_MATERIAL_NAMES,
  PLAN_PIPE_MATERIALS,
  VESSEL_LIMITS,
  genericBufferById,
  genericCylinderById,
  litresText,
  pipeMaterialOf,
  vesselCaption,
  type BufferRole,
  type FittingSpec,
  type PlanBufferSpec,
  type PlanCylinderSpec,
  type PlanPipeMaterial,
  RADIATOR_MOUNTING_HEIGHT_LIMITS_MM,
  RADIATOR_PLAN_RANGE,
  heatPumpSpecFor,
  outdoorUnitById,
  outdoorUnitName,
  radiatorDepthMm,
  radiatorHeights,
  radiatorLengths,
  radiatorPanelTypes,
  radiatorSpecFor,
  type FittingKind,
  type HeatPumpSpec,
  type OutdoorUnit,
  type PanelRadiatorType,
  type PipeCircuit,
  type PipeDiameterMm,
  type PipeSpec,
  type RadiatorSpec,
} from "@sts/rules-data";
import { formatLength } from "@sts/scan-viewer";
import type { DesignCheckResult, RadiatorUpgrade } from "@sts/calc";
import type { HeatPumpDesignInputs } from "@sts/rules-data";
import type { PlanObject } from "../api/types.js";
import { DesignCheckSummary, HeatPumpDesignEditor, RadiatorDesignDetails } from "./DesignCheckPanel.jsx";
import type { DesignCheckState } from "./use-design-check.js";
import { metres, type FitSuggestion, type ObjectFit } from "./plan-fit.js";
import {
  KIND_NAMES,
  objectCaption,
  pipeTotals,
  runLengthM,
  type ObjectTool,
} from "./plan-objects.js";
import { isProvisional, type PlanObjectsStatus } from "./use-plan-objects.js";

/**
 * The plan's Objects section: what can be put on the drawing, what is on it,
 * and the one selected.
 *
 * The same shape as `PlanDimensionsPanel` beside it, for the same reasons: it
 * is the keyboard and screen-reader route to every operation the drawing
 * offers by pointer (select, label, rotate, remove), and it is where each
 * figure's provenance is stated — the datasheet a heat pump's size came from,
 * whose depth a radiator is drawn at — next to the thing it applies to.
 *
 * Sized by its container, never by its own content: the drawing is fitted to
 * the space this column leaves, and a panel that grew when a tool was armed
 * would move the plan under the cursor (424e08d).
 */

type RadiatorChoice = { panelType: PanelRadiatorType; heightMm: number; lengthMm: number };

/** Quick picks — the sizes a designer reaches for first — shown only where the range sells them. */
const RADIATOR_QUICK_PICKS: readonly RadiatorChoice[] = [
  { panelType: "K2", heightMm: 600, lengthMm: 1200 },
  { panelType: "K2", heightMm: 600, lengthMm: 1000 },
  { panelType: "K3", heightMm: 600, lengthMm: 1200 },
  { panelType: "K1", heightMm: 600, lengthMm: 800 },
  { panelType: "K2", heightMm: 450, lengthMm: 1400 },
];

const CUSTOM = "custom";

function nearest(values: readonly number[], wanted: number): number {
  let best = values[0] ?? wanted;
  for (const v of values) if (Math.abs(v - wanted) < Math.abs(best - wanted)) best = v;
  return best;
}

/** The unit catalogue grouped by manufacturer and range, for an <optgroup> each. */
function groupedUnits(): readonly { label: string; units: readonly OutdoorUnit[] }[] {
  const groups = new Map<string, OutdoorUnit[]>();
  for (const unit of OUTDOOR_UNITS) {
    const key = `${unit.manufacturer} ${unit.range}`;
    const list = groups.get(key) ?? [];
    list.push(unit);
    groups.set(key, list);
  }
  return [...groups.entries()].map(([label, units]) => ({ label, units }));
}

interface CustomUnit {
  name: string;
  widthMm: string;
  depthMm: string;
  heightMm: string;
}

interface CustomVessel {
  name: string;
  volumeLitres: string;
  diameterMm: string;
  heightMm: string;
  /** Cylinders only. Empty: not known. */
  coilVolumeLitres: string;
}

/** A hand-entered vessel's figures, checked against the API's typo bounds; a message when one is out. */
function vesselFigures(custom: CustomVessel): { volumeLitres: number; diameterMm: number; heightMm: number } | string {
  const read = (value: string, what: string, limits: { min: number; max: number }, unit: string): number | string => {
    const n = Number(value);
    if (value.trim() === "" || !Number.isFinite(n) || n < limits.min || n > limits.max) {
      return `${what} must be from ${limits.min} to ${limits.max} ${unit}.`;
    }
    return n;
  };
  const v = read(custom.volumeLitres, "Volume", VESSEL_LIMITS.volumeLitres, "litres");
  const d = read(custom.diameterMm, "Diameter", VESSEL_LIMITS.diameterMm, "mm");
  const h = read(custom.heightMm, "Height", VESSEL_LIMITS.heightMm, "mm");
  for (const x of [v, d, h]) if (typeof x === "string") return x;
  return { volumeLitres: v as number, diameterMm: d as number, heightMm: h as number };
}

function customCylinderSpec(custom: CustomVessel): PlanCylinderSpec | string {
  const figures = vesselFigures(custom);
  if (typeof figures === "string") return figures;
  let coil: number | null = null;
  if (custom.coilVolumeLitres.trim() !== "") {
    const n = Number(custom.coilVolumeLitres);
    const { min, max } = VESSEL_LIMITS.coilVolumeLitres;
    if (!Number.isFinite(n) || n < min || n > max) return `Coil volume must be from ${min} to ${max} litres, or empty if not known.`;
    coil = n;
  }
  return { genericId: null, name: custom.name.trim() || "Hot water cylinder", ...figures, coilVolumeLitres: coil };
}

function customBufferSpec(custom: CustomVessel, role: BufferRole): PlanBufferSpec | string {
  const figures = vesselFigures(custom);
  if (typeof figures === "string") return figures;
  return { genericId: null, role, name: custom.name.trim() || BUFFER_ROLE_NAMES[role], ...figures };
}

function customSpec(custom: CustomUnit): HeatPumpSpec | string {
  const read = (value: string, what: string): number | string => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < CUSTOM_UNIT_LIMITS_MM.min || n > CUSTOM_UNIT_LIMITS_MM.max) {
      return `${what} must be a whole number of millimetres from ${CUSTOM_UNIT_LIMITS_MM.min} to ${CUSTOM_UNIT_LIMITS_MM.max}.`;
    }
    return n;
  };
  const w = read(custom.widthMm, "Width");
  const d = read(custom.depthMm, "Depth");
  const h = read(custom.heightMm, "Height");
  for (const v of [w, d, h]) if (typeof v === "string") return v;
  const name = custom.name.trim() || "Custom outdoor unit";
  return { catalogueId: null, name, widthMm: w as number, depthMm: d as number, heightMm: h as number };
}

export interface PlanObjectsPanelProps {
  readonly status: PlanObjectsStatus;
  readonly loadError: string | null;
  readonly onReload: () => void;
  readonly tool: ObjectTool | null;
  readonly onArm: (tool: ObjectTool | null) => void;
  /** Points laid so far on the pipe being drawn. */
  readonly runPoints: number;
  readonly runLengthM: number;
  /** This storey's objects. */
  readonly objects: readonly PlanObject[];
  /** Every storey's, for the pipe totals. */
  readonly allObjects: readonly PlanObject[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string | null) => void;
  readonly onRotate: (id: string) => void;
  readonly onRemove: (id: string) => void;
  readonly onLabel: (id: string, label: string | null) => void;
  readonly onPipeSpec: (id: string, spec: PipeSpec) => void;
  /** A placed fitting's spec changed — a reducer's sizes. */
  readonly onFittingSpec?: (id: string, spec: FittingSpec) => void;
  readonly error: string | null;
  readonly onDismissError: () => void;
  /** This storey's fit checks, by object id (`plan-fit.ts`). */
  readonly fits?: ReadonlyMap<string, ObjectFit>;
  readonly onApplySuggestion?: (id: string, suggestion: FitSuggestion) => void;
  /** A placed radiator's size, type or mounting height changed. */
  readonly onRadiatorSpec?: (id: string, spec: RadiatorSpec) => void;
  /** The MCS design check: its summary at the top, its details with the selected object. */
  readonly design?: PlanDesignProps;
  /**
   * May see the plan's objects but not change them — the matrix's
   * `design: update`, which an installer on the job does not hold. The list,
   * the fit and the design check are shown; the palette, every editor and
   * every remove button are not rendered at all.
   */
  readonly readOnly?: boolean;
}

export interface PlanDesignProps {
  readonly state: DesignCheckState;
  readonly result: DesignCheckResult | undefined;
  readonly onPickRoom: (story: number, index: number) => void;
  readonly onApplyUpgrade: (upgrade: RadiatorUpgrade) => void;
  readonly onHeatPumpDesign: (id: string, design: HeatPumpDesignInputs) => void;
  readonly onPlacePosition: (unitId: string, positionId: string, isNew: boolean) => void;
  /** The assessment position being placed on the plan, if one is. */
  readonly placingPositionId: string | null;
  /** Set when the plan has been corrected since the heat loss was calculated: what to say. */
  readonly planEdited?: string | null;
  /** Names someone typed for storeys, by level, for the per-storey emitter panel. */
  readonly storeyLabels?: ReadonlyMap<number, string>;
}

const NO_FITS: ReadonlyMap<string, ObjectFit> = new Map();

export function PlanObjectsPanel(props: PlanObjectsPanelProps): ReactNode {
  const { status, tool, onArm, objects, selectedId } = props;
  const fits = props.fits ?? NO_FITS;
  const available = status === "ready";

  const [unitId, setUnitId] = useState<string>(OUTDOOR_UNITS[0]?.id ?? CUSTOM);
  const [custom, setCustom] = useState<CustomUnit>({
    name: "",
    widthMm: "1000",
    depthMm: "400",
    heightMm: "900",
  });
  const [radiator, setRadiator] = useState<RadiatorChoice>({
    panelType: "K2",
    heightMm: 600,
    lengthMm: 1200,
  });
  const [pipe, setPipe] = useState<PipeSpec>({ diameterMm: 15, circuit: "flow", material: "copper" });
  const [fitting, setFitting] = useState<FittingKind>("elbow");
  // A reducer placed away from a joint reduces to this; at a joint it takes the two sizes meeting there.
  const [reducedMm, setReducedMm] = useState<PipeDiameterMm>(15);
  const [cylinderId, setCylinderId] = useState<string>("cylinder-210");
  const [customCylinder, setCustomCylinder] = useState<CustomVessel>({
    name: "",
    volumeLitres: "200",
    diameterMm: "550",
    heightMm: "1500",
    coilVolumeLitres: "",
  });
  const [bufferId, setBufferId] = useState<string>("buffer-60");
  const [bufferRole, setBufferRole] = useState<BufferRole>("buffer");
  const [customBuffer, setCustomBuffer] = useState<CustomVessel>({
    name: "",
    volumeLitres: "50",
    diameterMm: "400",
    heightMm: "800",
    coilVolumeLitres: "",
  });
  const [open, setOpen] = useState<ObjectTool["kind"] | null>(null);

  const groups = useMemo(groupedUnits, []);
  const unit = unitId === CUSTOM ? undefined : outdoorUnitById(unitId);
  const heatPump: HeatPumpSpec | string = unit ? heatPumpSpecFor(unit) : customSpec(custom);
  const radiatorSpec = radiatorSpecFor(radiator.panelType, radiator.heightMm, radiator.lengthMm);
  const cylinderSpec = (id: string, c: CustomVessel): PlanCylinderSpec | string =>
    id === CUSTOM ? customCylinderSpec(c) : (genericCylinderById(id) ?? "Pick a size.");
  const bufferSpec = (id: string, c: CustomVessel, role: BufferRole): PlanBufferSpec | string => {
    if (id === CUSTOM) return customBufferSpec(c, role);
    const found = genericBufferById(id);
    return found ? { ...found, role } : "Pick a size.";
  };
  const cylinder = cylinderSpec(cylinderId, customCylinder);
  const buffer = bufferSpec(bufferId, customBuffer, bufferRole);

  const shown = tool?.kind ?? open;

  function toolFor(kind: ObjectTool["kind"], overrides: {
    heatPump?: HeatPumpSpec | string;
    radiator?: RadiatorChoice;
    pipe?: PipeSpec;
    fitting?: FittingKind;
    reducedMm?: PipeDiameterMm;
    cylinder?: PlanCylinderSpec | string;
    buffer?: PlanBufferSpec | string;
  } = {}): ObjectTool | null {
    switch (kind) {
      case "heat-pump": {
        const spec = overrides.heatPump ?? heatPump;
        return typeof spec === "string" ? null : { kind, spec };
      }
      case "radiator": {
        const r = overrides.radiator ?? radiator;
        const spec = radiatorSpecFor(r.panelType, r.heightMm, r.lengthMm);
        return spec ? { kind, spec } : null;
      }
      case "pipe":
        return { kind, spec: overrides.pipe ?? pipe };
      case "fitting": {
        const which = overrides.fitting ?? fitting;
        return {
          kind,
          spec:
            which === "reducer"
              ? { fitting: which, diameterMm: null, reducedDiameterMm: overrides.reducedMm ?? reducedMm }
              : { fitting: which, diameterMm: null },
        };
      }
      case "cylinder": {
        const spec = overrides.cylinder ?? cylinder;
        return typeof spec === "string" ? null : { kind, spec };
      }
      case "buffer": {
        const spec = overrides.buffer ?? buffer;
        return typeof spec === "string" ? null : { kind, spec };
      }
    }
  }

  /** A choice changed: if that kind's tool is in hand, it now puts down the new choice. */
  function rearm(kind: ObjectTool["kind"], overrides: Parameters<typeof toolFor>[1]): void {
    if (tool?.kind !== kind) return;
    onArm(toolFor(kind, overrides));
  }

  function pick(kind: ObjectTool["kind"]): void {
    if (tool?.kind === kind) {
      onArm(null);
      setOpen(kind);
      return;
    }
    setOpen(kind);
    onArm(toolFor(kind));
  }

  function chooseRadiator(next: RadiatorChoice): void {
    const heights = radiatorHeights(next.panelType);
    const heightMm = heights.includes(next.heightMm) ? next.heightMm : nearest(heights, next.heightMm);
    const lengths = radiatorLengths(next.panelType, heightMm);
    const lengthMm = lengths.includes(next.lengthMm) ? next.lengthMm : nearest(lengths, next.lengthMm);
    const fixed = { panelType: next.panelType, heightMm, lengthMm };
    setRadiator(fixed);
    rearm("radiator", { radiator: fixed });
  }

  const selected = objects.find((o) => o.id === selectedId) ?? null;
  const totals = pipeTotals(props.allObjects);

  return (
    <aside
      className="floor-plan__panel floor-plan__panel--objects"
      aria-label="Objects on the plan"
      style={{ minWidth: 0 }}
    >
      {props.design ? (
        <DesignCheckSummary
          state={props.design.state}
          result={props.design.result}
          onSelectObject={(id) => props.onSelect(id)}
          onPickRoom={props.design.onPickRoom}
          planEdited={props.design.planEdited ?? null}
          readOnly={props.readOnly === true}
          storeyLabels={props.design.storeyLabels}
        />
      ) : null}

      <p className="floor-plan__panel-title">
        Objects <span className="muted">· {objects.length}</span>
      </p>

      {status === "unavailable" ? (
        <p className="floor-plan__panel-empty">
          Objects are saved with the survey, and this view is not connected to one.
        </p>
      ) : status === "failed" ? (
        <p className="floor-plan__panel-error" role="alert">
          The plan&apos;s objects could not be loaded. {props.loadError}{" "}
          <button type="button" className="floor-plan__link" onClick={props.onReload}>
            Try again
          </button>
        </p>
      ) : null}

      {/* Read-only (an installer on the job): no palette and no tool options —
          what is on the plan is listed below, and nothing offers to change it. */}
      {props.readOnly ? null : (
      <>
      <div className="floor-plan__palette" role="group" aria-label="Add to the plan">
        {(["heat-pump", "radiator", "pipe", "fitting", "cylinder", "buffer"] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            className="floor-plan__palette-tool"
            aria-pressed={tool?.kind === kind}
            disabled={!available}
            onClick={() => pick(kind)}
          >
            {kind === "heat-pump" ? "Heat pump" : KIND_NAMES[kind]}
          </button>
        ))}
      </div>

      {available && shown === "heat-pump" ? (
        <fieldset className="floor-plan__options">
          <legend className="visually-hidden">Heat pump outdoor unit</legend>
          <label className="floor-plan__field">
            <span>Outdoor unit</span>
            <select
              value={unitId}
              onChange={(event) => {
                const id = event.target.value;
                setUnitId(id);
                const chosen = id === CUSTOM ? undefined : outdoorUnitById(id);
                rearm("heat-pump", {
                  heatPump: chosen ? heatPumpSpecFor(chosen) : customSpec(custom),
                });
              }}
            >
              {groups.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {outdoorUnitName(u)}
                    </option>
                  ))}
                </optgroup>
              ))}
              <option value={CUSTOM}>Custom size…</option>
            </select>
          </label>
          {unit ? (
            <p className="floor-plan__source" data-testid="heat-pump-source">
              {unit.widthMm} × {unit.depthMm} mm footprint, {unit.heightMm} mm high (W × D × H,{" "}
              {unit.modelCodes}). From{" "}
              <a href={unit.source.url} target="_blank" rel="noreferrer">
                {unit.source.document}
              </a>
              {unit.source.revision ? `, ${unit.source.revision}` : ""}, read {unit.source.accessed}.
              {unit.source.note ? ` ${unit.source.note}` : ""}
            </p>
          ) : (
            <div className="floor-plan__custom">
              <label className="floor-plan__field">
                <span>Name</span>
                <input
                  value={custom.name}
                  maxLength={120}
                  placeholder="Custom outdoor unit"
                  onChange={(event) => {
                    const next = { ...custom, name: event.target.value };
                    setCustom(next);
                    rearm("heat-pump", { heatPump: customSpec(next) });
                  }}
                />
              </label>
              {(["widthMm", "depthMm", "heightMm"] as const).map((field) => (
                <label key={field} className="floor-plan__field floor-plan__field--mm">
                  <span>{field === "widthMm" ? "Width" : field === "depthMm" ? "Depth" : "Height"} (mm)</span>
                  <input
                    inputMode="numeric"
                    value={custom[field]}
                    onChange={(event) => {
                      const next = { ...custom, [field]: event.target.value };
                      setCustom(next);
                      const spec = customSpec(next);
                      if (tool?.kind === "heat-pump") onArm(typeof spec === "string" ? null : { kind: "heat-pump", spec });
                    }}
                  />
                </label>
              ))}
              {typeof heatPump === "string" ? (
                <p className="floor-plan__panel-error" role="status">{heatPump}</p>
              ) : (
                <p className="floor-plan__source">Your own figures — check them against the unit&apos;s datasheet.</p>
              )}
            </div>
          )}
        </fieldset>
      ) : null}

      {available && shown === "radiator" ? (
        <fieldset className="floor-plan__options">
          <legend className="visually-hidden">Radiator</legend>
          <div className="floor-plan__quick" role="group" aria-label="Common sizes">
            {RADIATOR_QUICK_PICKS.filter((p) => radiatorSpecFor(p.panelType, p.heightMm, p.lengthMm)).map((p) => {
              const on =
                p.panelType === radiator.panelType &&
                p.heightMm === radiator.heightMm &&
                p.lengthMm === radiator.lengthMm;
              return (
                <button
                  key={`${p.panelType}-${p.heightMm}-${p.lengthMm}`}
                  type="button"
                  className="floor-plan__chip"
                  aria-pressed={on}
                  onClick={() => chooseRadiator(p)}
                >
                  {p.panelType} {p.heightMm}×{p.lengthMm}
                </button>
              );
            })}
          </div>
          <label className="floor-plan__field">
            <span>Type</span>
            <select
              value={radiator.panelType}
              onChange={(event) =>
                chooseRadiator({ ...radiator, panelType: event.target.value as PanelRadiatorType })
              }
            >
              {radiatorPanelTypes().map((type) => (
                <option key={type} value={type}>
                  {type} ({PANEL_TYPE_ALIASES[type]})
                </option>
              ))}
            </select>
          </label>
          <label className="floor-plan__field">
            <span>Height (mm)</span>
            <select
              value={radiator.heightMm}
              onChange={(event) => chooseRadiator({ ...radiator, heightMm: Number(event.target.value) })}
            >
              {radiatorHeights(radiator.panelType).map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </label>
          <label className="floor-plan__field">
            <span>Length (mm)</span>
            <select
              value={radiator.lengthMm}
              onChange={(event) => chooseRadiator({ ...radiator, lengthMm: Number(event.target.value) })}
            >
              {radiatorLengths(radiator.panelType, radiator.heightMm).map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <p className="floor-plan__source">
            Drawn {radiatorSpec?.lengthMm ?? radiator.lengthMm} mm long ×{" "}
            {radiatorDepthMm(radiator.panelType)} mm deep — the depth is nominal, from{" "}
            <a href={RADIATOR_PLAN_RANGE.url} target="_blank" rel="noreferrer">
              {RADIATOR_PLAN_RANGE.brand} {RADIATOR_PLAN_RANGE.range}
            </a>{" "}
            ({RADIATOR_PLAN_RANGE.revision}); other brands differ by a few millimetres. Sizes are the ones
            that range is sold in.
          </p>
        </fieldset>
      ) : null}

      {available && shown === "pipe" ? (
        <fieldset className="floor-plan__options">
          <legend className="visually-hidden">Pipe</legend>
          <div className="floor-plan__quick" role="group" aria-label="Pipe diameter">
            {PIPE_DIAMETERS_MM.map((d) => (
              <button
                key={d}
                type="button"
                className="floor-plan__chip"
                aria-pressed={pipe.diameterMm === d}
                onClick={() => {
                  const next = { ...pipe, diameterMm: d };
                  setPipe(next);
                  rearm("pipe", { pipe: next });
                }}
              >
                {d} mm
              </button>
            ))}
          </div>
          <div className="floor-plan__quick" role="group" aria-label="Circuit">
            {PIPE_CIRCUITS.map((c) => (
              <button
                key={c}
                type="button"
                className={`floor-plan__chip floor-plan__chip--${c}`}
                aria-pressed={pipe.circuit === c}
                onClick={() => {
                  const next = { ...pipe, circuit: c };
                  setPipe(next);
                  rearm("pipe", { pipe: next });
                }}
              >
                {c === "flow" ? "Flow" : "Return"}
              </button>
            ))}
          </div>
          <div className="floor-plan__quick" role="group" aria-label="Pipe material">
            {PLAN_PIPE_MATERIALS.map((m) => (
              <button
                key={m}
                type="button"
                className="floor-plan__chip"
                aria-pressed={pipeMaterialOf(pipe) === m}
                onClick={() => {
                  const next = { ...pipe, material: m };
                  setPipe(next);
                  rearm("pipe", { pipe: next });
                }}
              >
                {PIPE_MATERIAL_NAMES[m]}
              </button>
            ))}
          </div>
          <p className="floor-plan__source" data-testid="pipe-material-note">
            {materialNote(pipeMaterialOf(pipe))}
          </p>
        </fieldset>
      ) : null}

      {available && shown === "fitting" ? (
        <fieldset className="floor-plan__options">
          <legend className="visually-hidden">Fitting</legend>
          <label className="floor-plan__field">
            <span>Fitting</span>
            <select
              value={fitting}
              onChange={(event) => {
                const next = event.target.value as FittingKind;
                setFitting(next);
                rearm("fitting", { fitting: next });
              }}
            >
              {FITTING_KINDS.map((k) => (
                <option key={k} value={k}>
                  {FITTING_NAMES[k]}
                </option>
              ))}
            </select>
          </label>
          {fitting === "reducer" ? (
            <>
              <label className="floor-plan__field">
                <span>Reduces to (mm)</span>
                <select
                  aria-label="Reducer's smaller size, mm"
                  value={reducedMm}
                  onChange={(event) => {
                    const next = Number(event.target.value) as PipeDiameterMm;
                    setReducedMm(next);
                    rearm("fitting", { reducedMm: next });
                  }}
                >
                  {PIPE_DIAMETERS_MM.map((d) => (
                    <option key={d} value={d}>
                      {d} mm
                    </option>
                  ))}
                </select>
              </label>
              <p className="floor-plan__source">
                Dropped where two sizes of pipe meet, it takes both sizes from them. The stock report also adds a reducer
                wherever runs of different sizes meet and none is drawn.
              </p>
            </>
          ) : null}
        </fieldset>
      ) : null}

      {available && shown === "cylinder" ? (
        <fieldset className="floor-plan__options">
          <legend className="visually-hidden">Hot water cylinder</legend>
          <label className="floor-plan__field">
            <span>Cylinder</span>
            <select
              aria-label="Cylinder size"
              value={cylinderId}
              onChange={(event) => {
                const id = event.target.value;
                setCylinderId(id);
                rearm("cylinder", { cylinder: cylinderSpec(id, customCylinder) });
              }}
            >
              {GENERIC_CYLINDERS.map((c) => (
                <option key={c.genericId} value={c.genericId ?? ""}>
                  {c.name} (nominal)
                </option>
              ))}
              <option value={CUSTOM}>Custom size…</option>
            </select>
          </label>
          {cylinderId === CUSTOM ? (
            <VesselFields
              value={customCylinder}
              withCoil
              onChange={(next) => {
                setCustomCylinder(next);
                const spec = customCylinderSpec(next);
                if (tool?.kind === "cylinder") onArm(typeof spec === "string" ? null : { kind: "cylinder", spec });
              }}
              problem={typeof cylinder === "string" ? cylinder : null}
            />
          ) : typeof cylinder !== "string" ? (
            <VesselSource spec={cylinder} />
          ) : null}
        </fieldset>
      ) : null}

      {available && shown === "buffer" ? (
        <fieldset className="floor-plan__options">
          <legend className="visually-hidden">Buffer or volumiser</legend>
          <div className="floor-plan__quick" role="group" aria-label="Buffer or volumiser">
            {BUFFER_ROLES.map((role) => (
              <button
                key={role}
                type="button"
                className="floor-plan__chip"
                aria-pressed={bufferRole === role}
                onClick={() => {
                  setBufferRole(role);
                  rearm("buffer", { buffer: bufferSpec(bufferId, customBuffer, role) });
                }}
              >
                {/* Not just "Buffer": that is the palette's button. */}
                {role === "buffer" ? "Buffer tank" : BUFFER_ROLE_NAMES[role]}
              </button>
            ))}
          </div>
          <label className="floor-plan__field">
            <span>Size</span>
            <select
              aria-label="Buffer size"
              value={bufferId}
              onChange={(event) => {
                const id = event.target.value;
                setBufferId(id);
                rearm("buffer", { buffer: bufferSpec(id, customBuffer, bufferRole) });
              }}
            >
              {GENERIC_BUFFERS.map((b) => (
                <option key={b.genericId} value={b.genericId ?? ""}>
                  {b.name} — holds {litresText(b.volumeLitres)} L (nominal)
                </option>
              ))}
              <option value={CUSTOM}>Custom size…</option>
            </select>
          </label>
          {bufferId === CUSTOM ? (
            <VesselFields
              value={customBuffer}
              onChange={(next) => {
                setCustomBuffer(next);
                const spec = customBufferSpec(next, bufferRole);
                if (tool?.kind === "buffer") onArm(typeof spec === "string" ? null : { kind: "buffer", spec });
              }}
              problem={typeof buffer === "string" ? buffer : null}
            />
          ) : typeof buffer !== "string" ? (
            <VesselSource spec={buffer} />
          ) : null}
        </fieldset>
      ) : null}
      </>
      )}

      {tool ? (
        <p className="floor-plan__panel-hint" role="status">
          {hintFor(tool, props.runPoints, props.runLengthM)}
        </p>
      ) : props.design?.placingPositionId ? (
        <p className="floor-plan__panel-hint" role="status" data-testid="position-hint">
          Click where the neighbour&apos;s window is — 1 m outside the centre of the window. It can be outside the
          scanned rooms. The distance is measured across the plan from the unit; type the measured one to replace it.
          Esc cancels.
        </p>
      ) : null}

      <FitSummary objects={objects} fits={fits} selectedId={selectedId} onSelect={props.onSelect} />

      {selected ? (
        <SelectedObject
          object={selected}
          onRotate={props.onRotate}
          onRemove={props.onRemove}
          onLabel={props.onLabel}
          onPipeSpec={props.onPipeSpec}
          {...(props.onFittingSpec ? { onFittingSpec: props.onFittingSpec } : {})}
          {...(fits.has(selected.id) ? { fitCheck: fits.get(selected.id) } : {})}
          {...(props.onApplySuggestion ? { onApplySuggestion: props.onApplySuggestion } : {})}
          {...(props.onRadiatorSpec ? { onRadiatorSpec: props.onRadiatorSpec } : {})}
          {...(props.design ? { design: props.design } : {})}
          readOnly={props.readOnly === true}
        />
      ) : null}

      {props.error ? (
        <div className="floor-plan__panel-error" role="alert" data-testid="plan-object-error">
          {props.error}{" "}
          <button type="button" className="floor-plan__link" onClick={props.onDismissError}>
            Dismiss
          </button>
        </div>
      ) : null}

      {objects.length === 0 ? (
        available ? <p className="floor-plan__panel-empty">No objects on this storey yet.</p> : null
      ) : (
        <ul className="floor-plan__panel-list" aria-label="Objects on this storey">
          {objects.map((object) => {
            const caption = objectCaption(object);
            const on = object.id === selectedId;
            const saving = isProvisional(object.id);
            const misfit = (fits.get(object.id)?.problems.length ?? 0) > 0;
            return (
              <li
                key={object.id}
                className={on ? "floor-plan__panel-row is-selected" : "floor-plan__panel-row"}
              >
                <button
                  type="button"
                  className="floor-plan__object-name"
                  aria-pressed={on}
                  disabled={saving}
                  onClick={() => props.onSelect(on ? null : object.id)}
                >
                  {misfit ? (
                    <span className="floor-plan__fit-badge" aria-hidden="true" title="Does not fit where it is drawn">
                      !
                    </span>
                  ) : null}
                  <span className="floor-plan__object-kind">{KIND_NAMES[object.kind]}</span>{" "}
                  {object.label ? `${object.label} · ` : ""}
                  {caption}
                  {saving ? " (saving…)" : ""}
                  {misfit ? <span className="visually-hidden"> — does not fit</span> : null}
                </button>
                {props.readOnly ? null : (
                  <button
                    type="button"
                    className="floor-plan__panel-remove"
                    aria-label={`Remove the ${KIND_NAMES[object.kind].toLowerCase()} ${caption}`}
                    title="Remove"
                    disabled={saving}
                    onClick={() => props.onRemove(object.id)}
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {totals.length > 0 ? (
        <div className="floor-plan__totals" data-testid="pipe-totals">
          <p className="floor-plan__panel-subtitle">Pipe, all storeys</p>
          <table>
            <thead>
              <tr>
                <th scope="col">Size</th>
                <th scope="col">Flow</th>
                <th scope="col">Return</th>
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody>
              {totals.map((t) => (
                <tr key={`${t.diameterMm}:${t.material}`}>
                  <th scope="row">
                    {t.diameterMm} mm{t.material === "copper" ? "" : ` ${PIPE_MATERIAL_NAMES[t.material]}`}
                  </th>
                  <td>{formatLength(t.flowM, "m")}</td>
                  <td>{formatLength(t.returnM, "m")}</td>
                  <td>{formatLength(t.totalM, "m")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="floor-plan__source">
            Measured along the runs as drawn on the plan. Rises and drops between floors are not on a
            plan, so add them.
          </p>
        </div>
      ) : null}

      <p className="floor-plan__panel-note">
        Saved <strong>with the survey</strong>: colleagues see these objects. A layout drawing — it does
        not change the heat loss or the design pack.
      </p>
    </aside>
  );
}

function hintFor(tool: ObjectTool, runPoints: number, lengthM: number): string {
  switch (tool.kind) {
    case "heat-pump":
      return "Click to place the outdoor unit — outside the house is fine. Near a wall it turns to face away from it. R rotates it once placed. Esc finishes.";
    case "radiator":
      return "Click near a wall to put the radiator flat against it, or anywhere to place it free. Esc finishes.";
    case "pipe":
      return runPoints === 0
        ? "Click to start the run. Points snap to pipe ends, fittings, corners and walls."
        : `${formatLength(lengthM, "m")} so far. Click to add a bend; double-click or Enter to finish; Esc cancels this run.`;
    case "fitting":
      return "Click on a pipe to fit it there, in line with the pipe. Esc finishes.";
    case "cylinder":
      return "Click where the cylinder will stand — the circle is its diameter. Allow room round it for pipework and access. Esc finishes.";
    case "buffer":
      return "Click where the vessel will go — the circle is its diameter. Esc finishes.";
  }
}

/** What the plan says about a pipe material, next to the chips that pick it. */
function materialNote(material: PlanPipeMaterial): string {
  switch (material) {
    case "copper":
      return "EN 1057 copper, bought in 3 m lengths — the default, and what a run drawn before materials were recorded is.";
    case "pex-barrier":
      return "PEX barrier pipe, at copper's sizes. Bought in the coils its maker lists for the size (28 mm in straight lengths); clip it as its maker says.";
    case "multilayer":
      return "Multilayer is sized differently (16/20/26/32 mm): draw it at the copper size it replaces. No coil sizes are on record — set one in the stock allowances.";
  }
}

/** A generic vessel's figures and where they come from. */
function VesselSource({ spec }: { readonly spec: PlanCylinderSpec | PlanBufferSpec }): ReactNode {
  return (
    <p className="floor-plan__source" data-testid="vessel-source">
      {vesselCaption(spec)}, holds {litresText(spec.volumeLitres)} L
      {"coilVolumeLitres" in spec && spec.coilVolumeLitres !== null ? `, coil ${litresText(spec.coilVolumeLitres)} L` : ""}. Nominal —
      typical of one maker&apos;s range (
      <a href={GENERIC_VESSEL_SOURCE.url} target="_blank" rel="noreferrer">
        {GENERIC_VESSEL_SOURCE.document}
      </a>
      , {GENERIC_VESSEL_SOURCE.revision}), not a product choice: confirm it with the one you fit.
    </p>
  );
}

/** A hand-entered vessel's name and figures. */
function VesselFields({
  value,
  onChange,
  withCoil = false,
  problem,
}: {
  readonly value: CustomVessel;
  readonly onChange: (next: CustomVessel) => void;
  readonly withCoil?: boolean;
  readonly problem: string | null;
}): ReactNode {
  const field = (key: keyof CustomVessel, label: string) => (
    <label key={key} className="floor-plan__field floor-plan__field--mm">
      <span>{label}</span>
      <input
        inputMode={key === "name" ? "text" : "decimal"}
        value={value[key]}
        maxLength={key === "name" ? 120 : 8}
        onChange={(event) => onChange({ ...value, [key]: event.target.value })}
      />
    </label>
  );
  return (
    <div className="floor-plan__custom">
      {field("name", "Name")}
      {field("volumeLitres", "Volume (L)")}
      {field("diameterMm", "Diameter (mm)")}
      {field("heightMm", "Height (mm)")}
      {withCoil ? field("coilVolumeLitres", "Coil volume (L)") : null}
      {problem ? (
        <p className="floor-plan__panel-error" role="status">
          {problem}
        </p>
      ) : (
        <p className="floor-plan__source">Your own figures — from the product&apos;s datasheet.</p>
      )}
    </div>
  );
}

/**
 * The storey's fit problems, one line each with its figures, each a button
 * that selects the object it is about — or, with none, a line saying so.
 */
function FitSummary({
  objects,
  fits,
  selectedId,
  onSelect,
}: {
  readonly objects: readonly PlanObject[];
  readonly fits: ReadonlyMap<string, ObjectFit>;
  readonly selectedId: string | null;
  readonly onSelect: (id: string | null) => void;
}): ReactNode {
  const checked = objects.filter((object) => fits.has(object.id));
  if (checked.length === 0) return null;

  // An overlap is one problem between two objects: listed once, against the first of the pair.
  const rows: { object: PlanObject; message: string }[] = [];
  const pairs = new Set<string>();
  for (const object of checked) {
    for (const problem of fits.get(object.id)?.problems ?? []) {
      if (problem.kind === "overlaps" && problem.otherId) {
        const pair = [object.id, problem.otherId].sort().join("|");
        if (pairs.has(pair)) continue;
        pairs.add(pair);
      }
      rows.push({ object, message: problem.message });
    }
  }
  const unchecked = checked.filter((object) =>
    fits.get(object.id)?.notes.some((note) => note.kind === "sill-unknown"),
  ).length;
  const uncheckedLine =
    unchecked > 0
      ? ` Sill height could not be checked for ${unchecked} ${unchecked === 1 ? "radiator" : "radiators"} — select it to see why.`
      : "";

  if (rows.length === 0) {
    return (
      <p className="floor-plan__fit floor-plan__fit--ok" data-testid="fit-summary">
        <span className="floor-plan__fit-title">No fit problems found on this storey.</span>
        {uncheckedLine}
      </p>
    );
  }
  return (
    <section className="floor-plan__fit" aria-label="Fit problems on this storey" data-testid="fit-summary">
      <p className="floor-plan__fit-title">
        {rows.length} fit {rows.length === 1 ? "problem" : "problems"}
      </p>
      <ul className="floor-plan__fit-list">
        {rows.map((row, i) => (
          <li key={`${row.object.id}-${i}`}>
            <button
              type="button"
              className="floor-plan__fit-item"
              aria-current={row.object.id === selectedId ? "true" : undefined}
              onClick={() => onSelect(row.object.id)}
            >
              <strong>
                {KIND_NAMES[row.object.kind]} {objectCaption(row.object)}
              </strong>
              : {row.message}
            </button>
          </li>
        ))}
      </ul>
      <p className="floor-plan__fit-note">
        Advisory: checked against the scan&apos;s walls, doors and windows. Nothing stops you saving — you may know
        something the scan does not.{uncheckedLine}
      </p>
    </section>
  );
}

/** The selected object's fit: what is wrong, sizes that fix it, and what could not be checked. */
function FitDetails({
  object,
  fitCheck,
  saving,
  onApply,
}: {
  readonly object: PlanObject;
  readonly fitCheck: ObjectFit;
  readonly saving: boolean;
  readonly onApply?: (id: string, suggestion: FitSuggestion) => void;
}): ReactNode {
  const { problems, suggestions, notes } = fitCheck;
  const ok = problems.length === 0;
  return (
    <div className={ok ? "floor-plan__fit floor-plan__fit--ok" : "floor-plan__fit"} data-testid="selected-fit">
      <p className="floor-plan__fit-title">
        {!ok
          ? "Does not fit here"
          : fitCheck.clearRunM !== undefined
            ? `Fits: ${metres(fitCheck.clearRunM)} of clear wall here.`
            : "No fit problems found."}
      </p>
      {problems.map((problem, i) => (
        <p key={i} className="floor-plan__fit-problem">
          {problem.message}
        </p>
      ))}
      {suggestions.length > 0 && onApply ? (
        <div className="floor-plan__fit-suggestions" role="group" aria-label="Sizes that fit">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.label}
              type="button"
              className="floor-plan__tool floor-plan__tool--primary"
              disabled={saving}
              onClick={() => onApply(object.id, suggestion)}
            >
              {suggestion.label}
            </button>
          ))}
        </div>
      ) : null}
      {fitCheck.noSuggestion ? <p className="floor-plan__fit-note">{fitCheck.noSuggestion}</p> : null}
      {notes.map((note, i) => (
        <p
          key={i}
          className={
            note.kind === "sill-unknown" ? "floor-plan__fit-note floor-plan__fit-note--unknown" : "floor-plan__fit-note"
          }
        >
          {note.message}
        </p>
      ))}
    </div>
  );
}

/** A placed radiator's size, and how high it hangs — the inputs its fit is checked with. */
function RadiatorEditor({
  object,
  saving,
  onChange,
}: {
  readonly object: Extract<PlanObject, { kind: "radiator" }>;
  readonly saving: boolean;
  readonly onChange: (id: string, spec: RadiatorSpec) => void;
}): ReactNode {
  const { spec } = object;
  const [mountError, setMountError] = useState<string | null>(null);

  function keepMounting(next: RadiatorSpec, mountingHeightMm = spec.mountingHeightMm): RadiatorSpec {
    return mountingHeightMm === undefined ? next : { ...next, mountingHeightMm };
  }

  function choose(next: RadiatorChoice): void {
    const heights = radiatorHeights(next.panelType);
    const heightMm = heights.includes(next.heightMm) ? next.heightMm : nearest(heights, next.heightMm);
    const lengths = radiatorLengths(next.panelType, heightMm);
    const lengthMm = lengths.includes(next.lengthMm) ? next.lengthMm : nearest(lengths, next.lengthMm);
    const found = radiatorSpecFor(next.panelType, heightMm, lengthMm);
    if (found) onChange(object.id, keepMounting(found));
  }

  function commitMounting(raw: string, input: HTMLInputElement): void {
    const text = raw.trim();
    const base = radiatorSpecFor(spec.panelType, spec.heightMm, spec.lengthMm);
    if (!base) return;
    if (text === "") {
      setMountError(null);
      if (spec.mountingHeightMm !== undefined) onChange(object.id, base);
      return;
    }
    const n = Number(text);
    const { min, max } = RADIATOR_MOUNTING_HEIGHT_LIMITS_MM;
    if (!Number.isInteger(n) || n < min || n > max) {
      setMountError(`Enter whole millimetres from ${min} to ${max}, or leave it empty to assume ${DEFAULT_RADIATOR_MOUNTING_HEIGHT_MM} mm.`);
      input.value = spec.mountingHeightMm === undefined ? "" : String(spec.mountingHeightMm);
      return;
    }
    setMountError(null);
    if (n !== spec.mountingHeightMm) onChange(object.id, keepMounting(base, n));
  }

  const current = { panelType: spec.panelType, heightMm: spec.heightMm, lengthMm: spec.lengthMm };
  return (
    <div className="floor-plan__quick" data-testid="radiator-editor">
      <label className="floor-plan__field">
        <span>Type</span>
        <select
          aria-label="Selected radiator's type"
          value={spec.panelType}
          disabled={saving}
          onChange={(event) => choose({ ...current, panelType: event.target.value as PanelRadiatorType })}
        >
          {radiatorPanelTypes().map((type) => (
            <option key={type} value={type}>
              {type} ({PANEL_TYPE_ALIASES[type]})
            </option>
          ))}
        </select>
      </label>
      <label className="floor-plan__field">
        <span>Height (mm)</span>
        <select
          aria-label="Selected radiator's height, mm"
          value={spec.heightMm}
          disabled={saving}
          onChange={(event) => choose({ ...current, heightMm: Number(event.target.value) })}
        >
          {radiatorHeights(spec.panelType).map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
      </label>
      <label className="floor-plan__field">
        <span>Length (mm)</span>
        <select
          aria-label="Selected radiator's length, mm"
          value={spec.lengthMm}
          disabled={saving}
          onChange={(event) => choose({ ...current, lengthMm: Number(event.target.value) })}
        >
          {radiatorLengths(spec.panelType, spec.heightMm).map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <label className="floor-plan__field floor-plan__field--mm">
        <span>Off the floor (mm)</span>
        <input
          aria-label="Selected radiator's height off the floor, mm"
          inputMode="numeric"
          placeholder={String(DEFAULT_RADIATOR_MOUNTING_HEIGHT_MM)}
          disabled={saving}
          key={`${object.id}:${spec.mountingHeightMm ?? ""}`}
          defaultValue={spec.mountingHeightMm ?? ""}
          onBlur={(event) => commitMounting(event.target.value, event.target)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            if (event.key === "Escape") {
              event.currentTarget.value = spec.mountingHeightMm === undefined ? "" : String(spec.mountingHeightMm);
              event.currentTarget.blur();
            }
          }}
        />
      </label>
      <p className="floor-plan__source" data-testid="mounting-height">
        {spec.mountingHeightMm === undefined
          ? `Underside ${DEFAULT_RADIATOR_MOUNTING_HEIGHT_MM} mm off the floor — assumed, until you set it. Used to check it clears a window sill.`
          : `Underside ${spec.mountingHeightMm} mm off the floor, as you set it.`}
      </p>
      {mountError ? (
        <p className="floor-plan__panel-error" data-testid="mounting-height-error">
          {mountError}
        </p>
      ) : null}
    </div>
  );
}

/** A placed reducer's two sizes. Only pairs that reduce (larger to smaller) are offered. */
function ReducerEditor({
  object,
  saving,
  onChange,
}: {
  readonly object: Extract<PlanObject, { kind: "fitting" }>;
  readonly saving: boolean;
  readonly onChange: (id: string, spec: FittingSpec) => void;
}): ReactNode {
  const { spec } = object;
  const larger = spec.diameterMm;
  const smaller = spec.reducedDiameterMm ?? null;
  const set = (nextLarger: PipeDiameterMm | null, nextSmaller: PipeDiameterMm | null) => {
    const keep = nextLarger !== null && nextSmaller !== null && nextSmaller >= nextLarger ? null : nextSmaller;
    onChange(object.id, { ...spec, diameterMm: nextLarger, reducedDiameterMm: keep });
  };
  return (
    <div className="floor-plan__quick" data-testid="reducer-editor">
      <label className="floor-plan__field">
        <span>From (mm)</span>
        <select
          aria-label="Reducer's larger size, mm"
          value={larger ?? ""}
          disabled={saving}
          onChange={(event) => set(event.target.value === "" ? null : (Number(event.target.value) as PipeDiameterMm), smaller)}
        >
          <option value="">Not set</option>
          {PIPE_DIAMETERS_MM.filter((d) => d > PIPE_DIAMETERS_MM[0]).map((d) => (
            <option key={d} value={d}>
              {d} mm
            </option>
          ))}
        </select>
      </label>
      <label className="floor-plan__field">
        <span>To (mm)</span>
        <select
          aria-label="Reducer's smaller size, mm"
          value={smaller ?? ""}
          disabled={saving}
          onChange={(event) => set(larger, event.target.value === "" ? null : (Number(event.target.value) as PipeDiameterMm))}
        >
          <option value="">Not set</option>
          {PIPE_DIAMETERS_MM.filter((d) => larger === null || d < larger).map((d) => (
            <option key={d} value={d}>
              {d} mm
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function SelectedObject({
  object,
  onRotate,
  onRemove,
  onLabel,
  onPipeSpec,
  onFittingSpec,
  fitCheck,
  onApplySuggestion,
  onRadiatorSpec,
  design,
  readOnly = false,
}: {
  readonly object: PlanObject;
  readonly onRotate: (id: string) => void;
  readonly onRemove: (id: string) => void;
  readonly onLabel: (id: string, label: string | null) => void;
  readonly onPipeSpec: (id: string, spec: PipeSpec) => void;
  readonly onFittingSpec?: (id: string, spec: FittingSpec) => void;
  readonly fitCheck?: ObjectFit;
  readonly onApplySuggestion?: (id: string, suggestion: FitSuggestion) => void;
  readonly onRadiatorSpec?: (id: string, spec: RadiatorSpec) => void;
  readonly design?: PlanDesignProps;
  readonly readOnly?: boolean;
}): ReactNode {
  const saving = isProvisional(object.id);
  const unit =
    object.kind === "heat-pump" && object.spec.catalogueId
      ? outdoorUnitById(object.spec.catalogueId)
      : undefined;
  return (
    <section className="floor-plan__selected" aria-label="Selected object" data-testid="selected-object">
      <p className="floor-plan__selected-title">
        {KIND_NAMES[object.kind]}: {objectCaption(object)}
      </p>
      {object.kind === "heat-pump" ? (
        <p className="floor-plan__source">
          {object.spec.widthMm} × {object.spec.depthMm} × {object.spec.heightMm} mm (W × D × H).{" "}
          {unit ? (
            <>
              From{" "}
              <a href={unit.source.url} target="_blank" rel="noreferrer">
                {unit.source.document}
              </a>
              {unit.source.revision ? `, ${unit.source.revision}` : ""}.
            </>
          ) : object.spec.catalogueId ? (
            "Catalogue figures as placed."
          ) : (
            "Figures entered by hand."
          )}
        </p>
      ) : null}
      {object.kind === "radiator" ? (
        <p className="floor-plan__source">
          {object.spec.heightMm} mm high × {object.spec.lengthMm} mm long × {object.spec.depthMm} mm deep
          (nominal, {RADIATOR_PLAN_RANGE.brand} {RADIATOR_PLAN_RANGE.range}).
        </p>
      ) : null}
      {fitCheck ? (
        <FitDetails
          object={object}
          fitCheck={fitCheck}
          saving={saving}
          {...(onApplySuggestion && !readOnly ? { onApply: onApplySuggestion } : {})}
        />
      ) : null}
      {object.kind === "radiator" && design?.result ? (
        <RadiatorDesignDetails
          room={design.result.rooms.rooms.find((r) => r.radiators.some((rad) => rad.objectId === object.id))}
          saving={saving}
          {...(readOnly ? {} : { onApply: design.onApplyUpgrade })}
        />
      ) : null}
      {object.kind === "radiator" && onRadiatorSpec && !readOnly ? (
        <RadiatorEditor object={object} saving={saving} onChange={onRadiatorSpec} />
      ) : null}
      {object.kind === "radiator" && readOnly ? (
        <p className="floor-plan__source" data-testid="mounting-height">
          {object.spec.mountingHeightMm === undefined
            ? `Underside height off the floor not set (${DEFAULT_RADIATOR_MOUNTING_HEIGHT_MM} mm assumed).`
            : `Underside ${object.spec.mountingHeightMm} mm off the floor.`}
        </p>
      ) : null}
      {object.kind === "heat-pump" && design?.result ? (
        <HeatPumpDesignEditor
          object={object}
          context={design.state.context}
          sizing={design.result.heatPump.units.find((u) => u.objectId === object.id)}
          unit={design.result.outdoorUnits.find((u) => u.objectId === object.id)}
          saving={saving}
          onDesign={design.onHeatPumpDesign}
          onPlacePosition={design.onPlacePosition}
          placingPositionId={design.placingPositionId}
          readOnly={readOnly}
        />
      ) : null}
      {object.kind === "pipe" && readOnly ? (
        <p className="floor-plan__source" data-testid="pipe-spec">
          {object.spec.diameterMm} mm {PIPE_MATERIAL_NAMES[pipeMaterialOf(object.spec)]},{" "}
          {object.spec.circuit === "flow" ? "flow" : "return"}: {formatLength(runLengthM(object.geometry.points), "m")} along{" "}
          {object.geometry.points.length - 1} {object.geometry.points.length === 2 ? "leg" : "legs"}.
        </p>
      ) : null}
      {object.kind === "pipe" && !readOnly ? (
        <div className="floor-plan__quick">
          <label className="floor-plan__field">
            <span>Diameter</span>
            <select
              value={object.spec.diameterMm}
              disabled={saving}
              onChange={(event) =>
                onPipeSpec(object.id, {
                  ...object.spec,
                  diameterMm: Number(event.target.value) as PipeDiameterMm,
                })
              }
            >
              {PIPE_DIAMETERS_MM.map((d) => (
                <option key={d} value={d}>
                  {d} mm
                </option>
              ))}
            </select>
          </label>
          <label className="floor-plan__field">
            <span>Circuit</span>
            <select
              value={object.spec.circuit}
              disabled={saving}
              onChange={(event) =>
                onPipeSpec(object.id, { ...object.spec, circuit: event.target.value as PipeCircuit })
              }
            >
              {PIPE_CIRCUITS.map((c) => (
                <option key={c} value={c}>
                  {c === "flow" ? "Flow" : "Return"}
                </option>
              ))}
            </select>
          </label>
          <label className="floor-plan__field">
            <span>Material</span>
            <select
              aria-label="Selected pipe's material"
              value={pipeMaterialOf(object.spec)}
              disabled={saving}
              onChange={(event) =>
                onPipeSpec(object.id, { ...object.spec, material: event.target.value as PlanPipeMaterial })
              }
            >
              {PLAN_PIPE_MATERIALS.map((m) => (
                <option key={m} value={m}>
                  {PIPE_MATERIAL_NAMES[m]}
                </option>
              ))}
            </select>
          </label>
          <p className="floor-plan__source">
            {formatLength(runLengthM(object.geometry.points), "m")} along {object.geometry.points.length - 1}{" "}
            {object.geometry.points.length === 2 ? "leg" : "legs"}. Drag a point to move it.
          </p>
        </div>
      ) : null}
      {object.kind === "fitting" && object.spec.fitting === "reducer" && onFittingSpec && !readOnly ? (
        <ReducerEditor object={object} saving={saving} onChange={onFittingSpec} />
      ) : null}
      {object.kind === "cylinder" || object.kind === "buffer" ? (
        object.spec.genericId !== null ? (
          <VesselSource spec={object.spec} />
        ) : (
          <p className="floor-plan__source" data-testid="vessel-source">
            {vesselCaption(object.spec)}, holds {litresText(object.spec.volumeLitres)} L
            {object.kind === "cylinder" && object.spec.coilVolumeLitres !== null
              ? `, coil ${litresText(object.spec.coilVolumeLitres)} L`
              : object.kind === "cylinder"
                ? " — coil volume not known, so it is not in the system volume"
                : ""}
            . Figures entered by hand.
          </p>
        )
      ) : null}
      {readOnly ? (
        object.label ? (
          <p className="floor-plan__source" data-testid="object-label">
            Label: <strong>{object.label}</strong>
          </p>
        ) : null
      ) : (
      <>
      <label className="visually-hidden" htmlFor={`plan-object-label-${object.id}`}>
        Label for the selected {KIND_NAMES[object.kind].toLowerCase()}
      </label>
      <input
        id={`plan-object-label-${object.id}`}
        className="floor-plan__panel-label"
        placeholder="Add a label (e.g. Lounge, under window)"
        maxLength={80}
        disabled={saving}
        key={`${object.id}:${object.label ?? ""}`}
        defaultValue={object.label ?? ""}
        onBlur={(event) => {
          const next = event.target.value.trim();
          if (next !== (object.label ?? "")) onLabel(object.id, next === "" ? null : next);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            event.currentTarget.value = object.label ?? "";
            event.currentTarget.blur();
          }
        }}
      />
      <div className="floor-plan__selected-actions">
        {object.kind !== "pipe" && object.kind !== "cylinder" && object.kind !== "buffer" ? (
          <button
            type="button"
            className="floor-plan__tool"
            disabled={saving}
            onClick={() => onRotate(object.id)}
            title="Rotate 90° (R). Shift+R turns 15°."
          >
            Rotate 90°
          </button>
        ) : null}
        <button
          type="button"
          className="floor-plan__tool floor-plan__tool--danger"
          disabled={saving}
          onClick={() => onRemove(object.id)}
          title="Remove (Delete)"
        >
          Remove
        </button>
      </div>
      </>
      )}
    </section>
  );
}
